import * as React from "react";
import { useState, useContext, useEffect } from "react";
import { makeStyles, Theme, createStyles } from "@mui/styles";
import {ValueContext} from '~common/components/form-def-field-fc';
import { DatePicker } from '@mui/x-date-pickers';
import {ICGFormControl} from '~common/components/icg-form-control';
import MomentUtils from '@date-io/moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import Input from '@mui/material/Input';
import {EvaluateDateLimit} from '~common/components/form-def-field-fc';
import { FromDateContext, ToDateContext }  from "~common/components/fields/date-range-fc";


const useStyles = makeStyles((theme: Theme) => 
  createStyles({
    dateField: {
      marginTop: 0,
      width: '100%',
    },
    displayText: {
      width: "100%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },
  })
);

interface DateProps {
  fieldDefinition: any;
  policyMessages?: any;
  contextType?: string;
  error?: any;
  readOnlyOverride?: boolean;
}

export const DateField: React.FC<DateProps> = ({
  fieldDefinition,
  policyMessages,
  contextType,
  readOnlyOverride
}) => {
  const classes = useStyles();    

  // Setting the inital value and setValue hook based on the context passed in from form def field, instead of useState
  // setFieldVal will pass the value up to the parent component that houses the context.
  var parentContext = useContext(ValueContext);
  if (contextType == "from") {
    parentContext = useContext(FromDateContext);

  } else if (contextType == "to") {
    parentContext = useContext(ToDateContext);
  }

  const [fieldVal, setFieldVal] = parentContext;

  // Make sure the field value is converted to a momment object before it is used to set local value 
  // so that we can UTC-ify it ourselves and not have MUI take any liberties with what timezone it thinks we want
  const mu = new MomentUtils();
  const d = (moment.utc(new Date(Date.parse(fieldVal))))

  const [localVal, setLocalVal] = useState(d || null);


  const onChange = (date) => {

    if (date && date.isValid && date.isValid()) {
      var newDate = new Date(Date.UTC(date.get('year'), date.get('month'), date.get('date'), 0, 0, 0, 0))
      setFieldVal(moment.utc(newDate));
      setLocalVal(moment.utc(newDate))
    } else {
      setLocalVal(moment.utc(date))
    }
  }

  const onBlur = (date) => {
    if (date && date.target && date.target.value) {
      const d = mu.parse(date.target.value, "MM/DD/YYYY");
      // If a user enteres a partial date and leaves the field, we should not try to parse it
      if (!d.isValid()){
        setFieldVal(null)
        setLocalVal(null)
      } else {
        // Be sure the date actually changed before calling this
        const dv = localVal
        if (!localVal || d.format("MM/DD/YYYY") != (new Date(localVal)).toLocaleDateString('en-US', {month: '2-digit', day: '2-digit', year: 'numeric'})) {
          if (d.isValid && d.isValid()) {              
              setFieldVal(d.utc().startOf('day') as any);
          } else {
            // We have a value, but cannot parse it, so default to today?
            const today = mu.moment(new Date());
            setFieldVal(today);
            setLocalVal({ dateVal: today });
          }
        }
      }
    } else {
      setLocalVal(null);
      setFieldVal(null);
    }
  }
  
  
    // Listen for changes from the parent def
    useEffect(() => {
      if (fieldVal) {
        setLocalVal(moment.utc(fieldVal));
      } else {
        setLocalVal(null);
      }
      
    }, [fieldVal])

  if (fieldDefinition.mode == "Display") {
    var val = moment.utc(fieldVal);
    var displayVal: any;

    if (val.isValid()) {
      displayVal = val.format("MM/DD/YYYY");
    }
    return (
    <ICGFormControl fieldDefinition={fieldDefinition} policyMessages={policyMessages}> 
        <div name={fieldDefinition.key} className={classes.displayText} >
          {displayVal || "--"}
        </div>
    </ICGFormControl>
    );
  }

  var minDate: any;
  var maxDate: any;
  // Check to see if we have min and max date
  if (fieldDefinition.minDate) {
    minDate = EvaluateDateLimit(fieldDefinition.minDate);
  }
  if (fieldDefinition.maxDate) {
    maxDate = EvaluateDateLimit(fieldDefinition.maxDate);
  }
  
  const innerField  = (inputRef, inputProps, InputProps) => { 
    return(
      <ICGFormControl fieldDefinition={fieldDefinition} policyMessages={policyMessages} readOnlyOverride={readOnlyOverride}> 
      <div>
        <Input
          placeholder={fieldDefinition.placeholder || ""}
          className={classes.dateField}
          ref={inputRef} 
          onChange={onChange}
          onBlur={onBlur}
          endAdornment={InputProps?.endAdornment}
          {...inputProps}

        />
        </div>
      </ICGFormControl> 
    )
  }





  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DatePicker
        InputAdornmentProps={{
          tabIndex: -1
        }}
        inputProps={{
          name: fieldDefinition.key,
        }}
        placeholder={fieldDefinition.placeholder == undefined ? 'MM/DD/YYYY' : fieldDefinition.placeholder}
      
        clearable={true}
        mask="__/__/____"
        margin="normal"
        inputFormat={fieldDefinition.format == undefined ? 'MM/DD/YYYY' : fieldDefinition.format}
        value={localVal}
        onChange={onChange}
        renderInput={({ inputRef, inputProps, InputProps }) => ( innerField(inputRef, inputProps, InputProps))}
        minDate={
          minDate ? mu.moment(minDate) : null || null
        }
        maxDate={
          maxDate ? mu.moment(maxDate) : null || null
        }
        disabled={fieldDefinition.mode == "Read Only" || readOnlyOverride}
      />
    </LocalizationProvider>

  )
};


export default DateField;
